import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Nav, NavItem, NavLink,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import {
  Search32, Delivery32, FileAdd32, Info32,
} from '@bphxd/ds-core-react/icons';
import {
  createOpenOrderClickTracker,
  findShipmentToggleOnTracker,
  openOrderToggleOffTracker,
} from '../../analytics/events';
import pageTitleConstant from '../../analytics/constantPageTitle';
import './_shipmentLookupPanel.scss';

const tabs = [
  {
    id: 'delivery',
    label: 'Look up a delivery',
    path: '/shipment',
    Icon: Delivery32,
  },
  {
    id: 'open-order',
    label: 'Look up an open order',
    path: '/find-open-order',
    Icon: FileAdd32,
  },
  {
    id: 'create-open-order',
    label: 'Create an open order',
    path: '/open-order',
    Icon: FileAdd32,
  },
];

const ShipmentLookupPanel = ({ activeTab, children, title, description }) => {
  const [showDisabledTabMessage, setShowDisabledTabMessage] = useState(false);
  const messageTimeout = useRef(null);

  useEffect(() => () => clearTimeout(messageTimeout.current), []);

  const showDisabledTabNotice = () => {
    setShowDisabledTabMessage(true);
    clearTimeout(messageTimeout.current);
    messageTimeout.current = setTimeout(() => setShowDisabledTabMessage(false), 3000);
  };

  return (
  <section className="shipment-lookup-panel">
    <header className="shipment-lookup-heading">
      {/* <div className="shipment-lookup-heading-icon">
        <Search32 aria-hidden="true" />
      </div> */}
      <div>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
    </header>

    <Nav tabs className="shipment-lookup-tabs" role="tablist" aria-label="Delivery options">
      {tabs.map(({ id, label, path, Icon }) => {
        const isActive = activeTab === id;
        const isDisabled = id === 'open-order' || id === 'create-open-order';
        let onClick;

        if (id === 'open-order' && activeTab === 'delivery') {
          onClick = () => findShipmentToggleOnTracker(pageTitleConstant.lookUpShipment);
        } else if (id === 'delivery' && activeTab === 'open-order') {
          onClick = () => openOrderToggleOffTracker(pageTitleConstant.lookUpOpenOrder);
        } else if (id === 'create-open-order') {
          onClick = () => createOpenOrderClickTracker(
            activeTab === 'open-order'
              ? pageTitleConstant.lookUpOpenOrder
              : pageTitleConstant.lookUpShipment,
          );
        }

        return (
          <NavItem key={id}>
            <NavLink
              tag={isDisabled ? 'span' : Link}
              to={isDisabled ? undefined : path}
              className={isDisabled ? 'disabled' : undefined}
              role="tab"
              aria-selected={isActive}
              aria-disabled={isDisabled}
              aria-current={isActive ? 'page' : undefined}
              tabIndex={0}
              data-testid={`${id}-tab`}
              active={isActive}
              onClick={(event) => {
                if (onClick) onClick(event);
                if (isDisabled) showDisabledTabNotice();
              }}
              onKeyDown={(event) => {
                if (isDisabled && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  if (onClick) onClick(event);
                  showDisabledTabNotice();
                }
              }}
            >
              <Icon aria-hidden="true" />
              <span>{label}</span>
            </NavLink>
          </NavItem>
        );
      })}
    </Nav>
    {showDisabledTabMessage && (
      <div className="shipment-lookup-disabled-message" role="status">
        <Info32 aria-hidden="true" />
        <span>
          The open order tab has been disabled, please contact admininstrator for further assistance.
        </span>
      </div>
    )}
    {children}
  </section>
  );
};

ShipmentLookupPanel.propTypes = {
  activeTab: PropTypes.oneOf(['delivery', 'open-order', 'create-open-order']).isRequired,
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
};

ShipmentLookupPanel.defaultProps = {
  title: 'Look up a delivery',
  description: 'You need to be online to complete this step.',
};

export default ShipmentLookupPanel;
